import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";
import { langStore, translate, useSelecting } from "@noli/business";
import { Flex, Text } from "@chakra-ui/react";

import TextArea from "@/components/TextArea";
import { SubWithTitle } from "@/components/SubPanel/WithTitle";
import { InfoText, LabelText } from "@/modules/editor/components/styleComponents/Texts";
import { setNegativePrompt, statusStore } from "@/stores/status";
import { Actions } from "@/utils/api";
import { Editor_Words } from "@/utils/lang/editor";
import { checkAIMeta, checkMeta, toast, useOwnToast } from "@/utils/misc";
import { injectNegativePrompt } from "@/utils/prompt";
import { SafeOp } from "@/utils/safeOp";
import { ImageWithPlaceHolder } from "@/components/Image/WithPlaceHolder";
import { parseModelType } from "@/modules/editor/utils/meta";
import { MODEL_TYPES_INFO } from "@/modules/editor/constant";
import useImageWithPlaceholder, {
  checkNoliboxAndMonkeyCondition,
} from "@/modules/editor/hooks/useImageWithPlaceholder";
import { GenerateBizType } from "@/types/creator";
import BizGenerateButton from "@/components/bussiness/BizGenerateButton";
import { GeneratePriceRules } from "@/modules/editor/hooks/useGenerateCost";
import { TooltipInfo } from "@/components/Icons";
import { MonkeyStore } from "@/thirdParty/monkey/store";
import { shallowCopy } from "@noli/core";
import { RequestModelParams } from "@/thirdParty/monkey/types";

/**
 * 去除图片内容
 */
const NegativePrompt: React.FC = () => {
  const lang = langStore.tgt;
  const t = useOwnToast();
  const { type, displayNode } = useSelecting("raw");
  const {
    url,
    placeholder,
    meta,
    enable: enableNegativePrompt,
  } = useImageWithPlaceholder({
    condition: checkNoliboxAndMonkeyCondition(),
    placeholderConfig: {
      noneSelect: Editor_Words["select-a-ai-picture"],
      notTargetTypeNode: (
        <TooltipInfo
          title={
            <Text lineHeight="15px">
              {translate(Editor_Words["current_select_not_for_negative_prompt"], lang)}
            </Text>
          }
          info={
            <Flex flexDir="column" fontSize="14px" lineHeight="22px" color="white">
              {translate(Editor_Words["negative_prompt_usable_tooltip_text"], lang)}
            </Flex>
          }
          tooltipProps={{
            arrowSize: 5,
            maxWidth: "250px",
          }}
        />
      ),
    },
  });
  const imgWH = useMemo(() => ({ w: meta?.data.w ?? 0, h: meta?.data.h ?? 0 }), [meta]);

  async function handleNegativePrompt(): Promise<void> {
    try {
      if (
        type !== "image" ||
        (checkMeta(meta) && !(checkAIMeta(meta) || meta.type === "monkey")) ||
        !meta?.data.promptInfo
      ) {
        toast(t, "warning", translate(Editor_Words["negative-prompt-not-support-message"], lang));
        return;
      }
      if (checkAIMeta(meta)) {
        // setup

        const busyAliases = [displayNode.alias];
        const virtualInfo = { w: meta.data.w, h: meta.data.h };
        const virtualNodeInfo = await SafeOp.setupPostCreator(
          t,
          "negative_prompt",
          lang,
          busyAliases,
          virtualInfo,
        );
        meta.data.promptInfo.originalNegative = statusStore.negativePrompt;
        const success = await injectNegativePrompt(t, meta);
        if (!success) {
          await SafeOp.cleanupSetup(busyAliases, virtualNodeInfo);
          throw Error;
        }
        await Actions.importMeta({
          t,
          lang,
          type: meta.type,
          meta,
          setupInfo: { checkBusy: false, busyAliases, virtualNodeInfo },
          opt: { freezePrompt: true },
          bizType: GenerateBizType.NegativePrompt,
        } as any);
      } else {
        if (meta.type === "monkey") {
          console.log("meta.data: ", meta.data);
          const newParams: RequestModelParams = shallowCopy(meta.data.params);
          newParams.negative_prompt = newParams.negative_prompt.concat(
            statusStore.negativePrompt ?? "",
          );
          // 调用猴子的接口
          MonkeyStore.postMonkeyTask({
            t,
            lang,
            params: newParams,
          });
        }
      }
    } catch {
      toast(t, "error", translate(Editor_Words["negative-prompt-error-message"], lang));
    }
  }

  const modelType = useMemo(() => (meta ? parseModelType(meta) : ""), [meta]);

  return (
    <SubWithTitle title={translate(Editor_Words["negative-prompt-title"], lang)}>
      <LabelText mt="16px">{translate(Editor_Words["choose-negaprompt-source"], lang)}:</LabelText>
      <ImageWithPlaceHolder url={url} placeholderText={placeholder} />
      <LabelText>{translate(Editor_Words["negaprompt-input"], lang)}:</LabelText>
      <TextArea
        w="100%"
        lang={lang}
        textAreaH={120}
        placeholderWord={Editor_Words["negative-prompt-placeholder"]}
        text={statusStore.negativePrompt}
        setText={setNegativePrompt}
        mt="8px"
      />
      {enableNegativePrompt ? (
        <InfoText fontSize="14px" mt="16px">
          <LabelText>{translate(Editor_Words["size"], lang)}：</LabelText>
          {translate(Editor_Words["stay-in-step-with-selected-pic"], lang)}
        </InfoText>
      ) : null}
      {enableNegativePrompt ? (
        <Flex flexDirection="column" gap="12px" mt="16px">
          <InfoText>
            {translate(Editor_Words["model"], lang)}：
            {modelType ? translate(MODEL_TYPES_INFO[modelType]?.showInfo, lang) : ""}
          </InfoText>
          <InfoText>
            {translate(Editor_Words["resolution"], lang)}： {imgWH.w} × {imgWH.h} px
          </InfoText>
        </Flex>
      ) : null}

      <BizGenerateButton
        sticky
        mt="20px"
        pb="32px"
        pt="16px"
        size={imgWH ?? { w: 0, h: 0 }}
        checkBusyConfig={{
          busyAlias: displayNode?.alias,
          checkAll: true,
        }}
        calcPriceProps={GeneratePriceRules[GenerateBizType.NegativePrompt]}
        buttonProps={{
          isDisabled: !enableNegativePrompt,
        }}
        onSubmit={handleNegativePrompt}
      >
        {translate(Editor_Words["negative-prompt-submit"], lang)}
      </BizGenerateButton>
    </SubWithTitle>
  );
};

export const NegativePromptPanel = observer(NegativePrompt);
